/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { publicHttpClient } from '@ahm/api-wrappers-core';
import { shouldAttachBearer } from './auth-interceptor-shared';
import { requestNewTokenFromApp } from './webinapp-request-token';
import { persistWebinappToken, readWebinappToken } from './webinapp-token';
import { useNewAuthStore } from '@/store/use-new-auth-store';

/**
 * Interceptor RIÊNG cho WEBINAPP (login bằng token trên URL từ app Ahamove).
 *
 * Vì sao tách hẳn khỏi web interceptor (install-axios-interceptor.ts):
 *  - Webinapp CHỈ có cookie `token`, KHÔNG có `refresh_token`. Refresh chain của
 *    web (/api/auth/refresh) sẽ luôn 401 VÀ xoá cookie token → logout oan.
 *  - Token webinapp chỉ "hoạt động" với BE SAU khi /api/auth/sync chạy xong. Trên
 *    thiết bị/webview chậm, call store/order có thể chạy TRƯỚC khi sync xong → BE
 *    trả 401 (đây là lý do bug "chỉ xảy ra ở một số thiết bị").
 *
 * Hành vi:
 *  - Request: gắn thẳng token từ cookie. KHÔNG gọi ensureValidToken (tránh trigger
 *    refresh chain → xoá cookie).
 *  - Response 401: re-activate session qua /api/auth/sync rồi retry 1 lần. Nếu vẫn
 *    fail → GIỮ phiên, reject (KHÔNG logout/redirect — webinapp không có web login;
 *    user mở lại từ app để lấy token mới).
 */

let installed = false;
let recoverInFlight: Promise<boolean> | null = null;

/**
 * Gọi từ new-auth-provider khi quan sát thấy token trên URL (entry từ app).
 * Persist token URL làm token CHÍNH (module var + localStorage bền + cookie
 * canonical) qua webinapp-token.ts — nguồn sự thật duy nhất cho token webinapp.
 */
export function setWebinappUrlToken(token: string | null) {
  if (token) persistWebinappToken(token);
}

/**
 * Token hiện tại của phiên webinapp: token URL/bền (readWebinappToken: mem →
 * localStorage → cookie). KHÔNG đọc cookie `token` trực tiếp (có thể nhập nhằng /
 * là token cũ của account khác → gốc bug gọi sync sai token).
 */
export function currentToken(): string {
  return readWebinappToken();
}

/**
 * Re-activate session webinapp: gọi lại /api/auth/sync với token hiện tại.
 * Idempotent (BE dedupe theo token); dedup concurrent callers phía client để
 * nhiều request 401 cùng lúc chỉ kích hoạt 1 lần sync.
 */
/** Activate (login) 1 token cụ thể với BE qua /api/auth/sync. */
async function activate(token: string): Promise<boolean> {
  if (!token) return false;
  try {
    const res = await fetch('/api/auth/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Khôi phục phiên webinapp khi 401 (deduped cho nhiều 401 song song):
 *  1. Activate token hiện tại — 401 thường chỉ vì token CHƯA activate phía BE.
 *  2. Vẫn fail → xin token MỚI từ app (JSFunction refreshToken) → persist
 *     (RAM+localStorage+cookie) → activate token mới.
 * true nếu 1 trong 2 bước ok; false → caller hiện dialog "mở lại từ app".
 */
export async function recoverWebinappSession(): Promise<boolean> {
  if (recoverInFlight) return recoverInFlight;
  recoverInFlight = (async () => {
    if (await activate(currentToken())) return true;

    const fresh = await requestNewTokenFromApp();
    if (fresh) {
      persistWebinappToken(fresh);
      if (await activate(fresh)) return true;
    }
    return false;
  })().finally(() => {
    setTimeout(() => {
      recoverInFlight = null;
    }, 1000);
  });
  return recoverInFlight;
}

export function installWebinappAuthInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  publicHttpClient.interceptors.request.use((config) => {
    if (
      config.headers &&
      !config.headers.Authorization &&
      shouldAttachBearer(config.url)
    ) {
      const token = currentToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  publicHttpClient.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      if (status !== 401 || !original || !shouldAttachBearer(original.url)) {
        return Promise.reject(error);
      }

      // Đã resync + retry 1 lần mà VẪN 401 → token webinapp hỏng thật (vd user đã
      // logout khỏi app). GIỮ phiên (không logout như web), nhưng bật cờ để hiện
      // dialog "mở lại từ app" — cách duy nhất làm mới token cho webinapp.
      if (original._retry) {
        useNewAuthStore.getState().setWebinappExpired(true);
        return Promise.reject(error);
      }
      original._retry = true;

      const ok = await recoverWebinappSession();
      if (!ok) {
        useNewAuthStore.getState().setWebinappExpired(true);
        return Promise.reject(error);
      }

      const token = currentToken();
      if (token && original.headers) {
        original.headers.Authorization = `Bearer ${token}`;
      }
      return publicHttpClient.request(original);
    }
  );
}
